import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import { Frequency, SavingsAccountParams } from "../../types/forms";
import { CurrencyField } from "./CurrencyField";

export default function SavingsInputs({
  accounts,
  onChange,
}: {
  accounts: SavingsAccountParams[] | undefined;
  onChange: (changes: { savingsAccounts: SavingsAccountParams[] }) => void;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [editingName, setEditingName] = useState<number | null>(null);

  const savingsAccounts: SavingsAccountParams[] =
    (accounts as SavingsAccountParams[]) || [];

  const handleAddAccount = () => {
    const accountNumber = savingsAccounts.length + 1;
    const newAccount: SavingsAccountParams = {
      name: `Account ${accountNumber}`,
      initialAmount: 0,
      interestRate: 0,
      contributionAmount: 0,
      contributionFrequency: "monthly",
      interestCompounding: "monthly",
    };
    onChange({ savingsAccounts: [...savingsAccounts, newAccount] });
  };

  const handleUpdateAccount = (
    index: number,
    updates: Partial<SavingsAccountParams>,
  ) => {
    const updated = savingsAccounts.map((acc, i) =>
      i === index ? { ...acc, ...updates } : acc,
    );
    onChange({ savingsAccounts: updated });
  };

  const handleRemoveAccount = (index: number) => {
    const updated = savingsAccounts.filter((_, i) => i !== index);
    onChange({ savingsAccounts: updated });
  };

  return (
    <Box sx={{ mt: 3, position: "relative" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Typography variant="h6">Savings & Investments</Typography>
        {isMobile ? (
          <Tooltip title="Add Account">
            <IconButton
              type="button"
              color="primary"
              onClick={handleAddAccount}
              size="large"
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            type="button"
            variant="outlined"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleAddAccount}
            size="medium"
          >
            Add Account
          </Button>
        )}
      </Box>

      {savingsAccounts.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Add one or more accounts to compare investing versus overpaying the
          mortgage. If no accounts are added, this section is ignored.
        </Typography>
      )}

      {savingsAccounts.map((acc: SavingsAccountParams, index: number) => (
        <Card
          key={index}
          variant="outlined"
          sx={{
            mb: 2,
            position: "relative",
            transition: "all 0.2s",
            "&:hover": {
              boxShadow: 2,
            },
          }}
        >
          <CardContent
            sx={{
              p: { xs: 2, sm: 3 },
              "&:last-child": { pb: { xs: 2, sm: 3 } },
            }}
          >
            {/* Account Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1,
                pl: 0.5,
              }}
            >
              {editingName === index ? (
                <TextField
                  autoFocus
                  value={acc.name || `Account ${index + 1}`}
                  onChange={(e) =>
                    handleUpdateAccount(index, { name: e.target.value })
                  }
                  onBlur={() => setEditingName(null)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") setEditingName(null);
                  }}
                  variant="standard"
                  sx={{
                    "& .MuiInputBase-input": {
                      fontSize: "1rem",
                      fontWeight: 500,
                    },
                  }}
                />
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="subtitle1"
                    component="h3"
                    fontWeight={600}
                  >
                    {acc.name || `Account ${index + 1}`}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setEditingName(index)}
                    sx={{ opacity: 0.6, "&:hover": { opacity: 1 } }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
              <IconButton
                aria-label="Remove account"
                color="error"
                onClick={() => handleRemoveAccount(index)}
                size="small"
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Box>

            {/* Primary Account Details */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                },
                gap: 2,
                mb: 2,
              }}
            >
              <TextField
                label="Interest Rate (%)"
                type="number"
                slotProps={{ htmlInput: { step: 0.01, min: 0 } }}
                value={acc.interestRate || ""}
                onChange={(e) =>
                  handleUpdateAccount(index, {
                    interestRate:
                      e.target.value === "" ? 0 : parseFloat(e.target.value),
                  })
                }
                margin="dense"
                placeholder="0"
              />
              <CurrencyField
                label="Contribution Amount"
                value={acc.contributionAmount ?? 0}
                onChange={(val) =>
                  handleUpdateAccount(index, { contributionAmount: val })
                }
                margin="dense"
                placeholderValue={0}
              />
            </Box>

            {/* Secondary Details & Frequencies */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(3, 1fr)",
                },
                gap: 2,
              }}
            >
              {/* Initial Balance */}
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 0.75, display: "block", mt: 0.5 }}
                >
                  Initial Balance
                </Typography>
                <CurrencyField
                  value={acc.initialAmount ?? 0}
                  onChange={(val) =>
                    handleUpdateAccount(index, { initialAmount: val })
                  }
                  size="small"
                  label={""}
                  placeholderValue={0}
                  showZero={true}
                />
              </Box>

              {/* Contribution Frequency */}
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 0.75, display: "block", mt: 0.5 }}
                >
                  Contribution Frequency
                </Typography>
                <ToggleButtonGroup
                  value={acc.contributionFrequency ?? "monthly"}
                  exclusive
                  onChange={(e, newValue) => {
                    if (newValue !== null) {
                      handleUpdateAccount(index, {
                        contributionFrequency: newValue as Frequency,
                      });
                    }
                  }}
                  fullWidth
                  size="small"
                >
                  <ToggleButton value="monthly">Monthly</ToggleButton>
                  <ToggleButton value="yearly">Yearly</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {/* Interest Compounding */}
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 0.75, display: "block", mt: 0.5 }}
                >
                  Interest Compounding
                </Typography>
                <ToggleButtonGroup
                  value={acc.interestCompounding ?? "monthly"}
                  exclusive
                  onChange={(e, newValue) => {
                    if (newValue !== null) {
                      handleUpdateAccount(index, {
                        interestCompounding: newValue as Frequency,
                      });
                    }
                  }}
                  fullWidth
                  size="small"
                >
                  <ToggleButton value="monthly">Monthly</ToggleButton>
                  <ToggleButton value="yearly">Yearly</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
}
